/**
 * RolesManager — Roles y permisos (estándar de listados del CMDB).
 *
 * ANTES
 * ---------------------------------------------------------------------------
 * Cabecera y tabla propias con clases de Bootstrap (`roles-page`, `table`),
 * modal propio para el alta, `window.confirm` para desactivar, sin buscador,
 * sin export y sin columnas elegibles. Además su CSS (`RolesManager.css`, ya
 * borrado) sobrescribía `.modal-header` / `.modal-footer` GLOBALES y pintaba el
 * modal con `--bg-card`, que en los skins de cristal deja ver lo de detrás.
 *
 * AHORA
 * ---------------------------------------------------------------------------
 * `ListPage` con buscador, filtros, columnas, vistas y export. El alta y la
 * edición (cinco campos) van en el diálogo único del CMDB, y el borrado —que en
 * este modelo es DESACTIVAR— usa la confirmación del estándar.
 *
 * DOS COSAS QUE NO ERAN LO QUE PARECÍAN
 * ---------------------------------------------------------------------------
 *  1. El campo «Ámbito» del formulario viejo **no existía en el backend**: la
 *     API crea siempre el rol en la empresa actual. El ámbito se DEDUCE de
 *     `company_id` (NULL = rol global) y se pinta, pero no se elige.
 *  2. El formulario ofrecía 10 tipos de objeto y la API solo aceptaba `server`
 *     (respondía 400 «object_type is not supported»). Ahora el backend acepta
 *     los 10 (son los mismos que usa la nomenclatura), pero **asignar** un rol
 *     a un equipo sigue siendo solo de servidores: es el único CI que hoy se
 *     resuelve. Por eso el botón de equipos sale desactivado en el resto.
 *
 * Botones propios:
 *  · **Ver los equipos con este rol** — diálogo con los servidores que lo
 *    tienen puesto (`GET /roles/<id>/assignments`).
 *  · **Copiar** — alta precargada con el mismo rol y el código con sufijo
 *    `_COPIA`, porque el código es único por empresa + tipo de objeto.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  IdentificationIcon,
  ListBulletIcon,
  Squares2X2Icon,
  ServerStackIcon,
  PlusIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import api from '../services/api';
import { OBJETOS, etiquetaObjeto } from '../config/objectTypes';

/**
 * Tipos de objeto del catálogo de roles. Salen del catálogo compartido
 * (`config/objectTypes.js`) y el backend valida con `ROLE_OBJECT_TYPES`, que
 * tiene la misma lista: si se añade un tipo allí, se añade aquí.
 */
const OBJECT_TYPES = OBJETOS;

/** Tipos a los que hoy se puede ASIGNAR un rol (el resto solo se catalogan). */
const ASSIGNABLE_TYPES = ['server'];

const EMPTY_ROLE = {
  name: '', code: '', object_type: 'server', priority: 100, description: '', is_active: true,
};

/** El código se guarda normalizado: mayúsculas y sin separadores ni acentos. */
function normalizeCode(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

const PRIORITY_LABEL = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  if (n >= 300) return 'muy alta';
  if (n >= 200) return 'alta';
  if (n >= 100) return 'normal';
  return 'baja';
};

export default function RolesManager({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [roles, setRoles]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [search, setSearch]   = useState('');
  const [notice, setNotice]   = useState('');

  const [filterType, setFilterType]         = useState('');
  const [filterStatus, setFilterStatus]     = useState('');
  const [filterScope, setFilterScope]       = useState('');
  const [filterAssigned, setFilterAssigned] = useState('');
  const [groupMode, setGroupMode]           = useState('');

  const [formOpen, setFormOpen]         = useState(false);
  const [editingRole, setEditingRole]   = useState(null);
  const [form, setForm]                 = useState({ ...EMPTY_ROLE });
  const [saving, setSaving]             = useState(false);
  const [formError, setFormError]       = useState('');
  const [copiedFrom, setCopiedFrom]     = useState(null);

  const [targetsOf, setTargetsOf]           = useState(null);
  const [targets, setTargets]               = useState([]);
  const [targetsLoading, setTargetsLoading] = useState(false);

  const typeLabel = useCallback(
    (value) => {
      if (!value) return '';
      const fromNaming = tr(`naming.object.${value}`, '');
      if (fromNaming) return fromNaming;
      if (value === 'chassis') return tr('naming.object.chassis', 'Chasis');
      return etiquetaObjeto(value) || value;
    },
    [tr],
  );

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      // Los filtros son de UI (estándar): se pide todo, incluidos los inactivos
      // y los roles globales, y se filtra en cliente.
      const res = await api.getRoles(companyId, { includeGlobal: true, includeInactive: true });
      setRoles(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.message || tr('roles.load_error', 'No se pudieron cargar los roles'));
    } finally {
      setLoading(false);
    }
  }, [companyId, tr]);

  useEffect(() => { load(); }, [load]);

  /* ?destacar=<id>[&abrir=1] — convención del CMDB. */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !roles.length) return undefined;
    const id = Number(raw);
    const target = roles.find(r => r.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) openEdit(target);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, roles, setSearchParams]);

  /* ── diálogo de alta / edición ──────────────────────────────────────────── */
  const openCreate = () => {
    setEditingRole(null);
    setCopiedFrom(null);
    setForm({ ...EMPTY_ROLE });
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (role) => {
    setEditingRole(role);
    setCopiedFrom(null);
    setForm({
      name: role.name || '',
      code: role.code || '',
      object_type: role.object_type || 'server',
      priority: role.priority ?? 100,
      description: role.description || '',
      is_active: role.is_active !== false,
    });
    setFormError('');
    setFormOpen(true);
  };

  /** Copiar: mismo rol con el código libre para no chocar (es único por tipo). */
  const openCopy = (role) => {
    const base = normalizeCode(role.code);
    setEditingRole(null);
    setCopiedFrom(role);
    setForm({
      name: role.name ? `${role.name} (copia)` : '',
      code: base ? `${base}_COPIA` : '',
      object_type: role.object_type || 'server',
      priority: role.priority ?? 100,
      description: role.description || '',
      is_active: true,
    });
    setFormError('');
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingRole(null);
    setCopiedFrom(null);
    setSaving(false);
  };

  const handleSave = async (event) => {
    event?.preventDefault();
    if (saving) return;
    const payload = {
      name: form.name.trim(),
      code: normalizeCode(form.code),
      object_type: form.object_type,
      priority: form.priority === '' ? null : Number(form.priority),
      description: form.description.trim() || null,
      is_active: form.is_active !== false,
    };
    if (!payload.name) { setFormError(tr('roles.name_required', 'El nombre es obligatorio.')); return; }
    if (!payload.code) { setFormError(tr('roles.code_required', 'El código es obligatorio.')); return; }

    setSaving(true);
    setFormError('');
    try {
      if (editingRole?.id) {
        await api.updateRole(editingRole.id, payload, companyId);
        setNotice(tr('roles.saved', 'Rol guardado'));
      } else {
        await api.createRole(payload, companyId);
        setNotice(tr('roles.created_ok', 'Rol creado'));
      }
      setTimeout(() => setNotice(''), 3000);
      closeForm();
      await load();
    } catch (err) {
      setFormError(err.message || tr('roles.save_error', 'No se pudo guardar el rol'));
    } finally {
      setSaving(false);
    }
  };

  /** En este modelo «eliminar» es DESACTIVAR: el rol deja de ofrecerse pero se
   *  conserva, porque los equipos que lo tienen puesto lo siguen apuntando. */
  const handleDelete = async (role) => {
    try {
      await api.deleteRole(role.id, companyId);
      setNotice(tr('roles.deleted', 'Rol desactivado'));
      setTimeout(() => setNotice(''), 3000);
      await load();
    } catch (err) {
      setError(err.message || tr('roles.delete_error', 'No se pudo desactivar el rol'));
    }
  };

  /* ── equipos con el rol ─────────────────────────────────────────────────── */
  const openTargets = async (role) => {
    setTargetsOf(role); setTargets([]); setTargetsLoading(true);
    try {
      const res = await api.getRoleTargets(role.id, companyId);
      setTargets(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError(err.message || tr('roles.targets_error', 'No se pudieron cargar los equipos'));
    } finally {
      setTargetsLoading(false);
    }
  };

  /* ── filtrado ───────────────────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return roles.filter((role) => {
      if (filterType && role.object_type !== filterType) return false;
      if (filterStatus === 'active' && role.is_active === false) return false;
      if (filterStatus === 'inactive' && role.is_active !== false) return false;
      if (filterScope === 'global' && role.company_id) return false;
      if (filterScope === 'company' && !role.company_id) return false;
      if (filterAssigned === 'with' && !role.assignment_count) return false;
      if (filterAssigned === 'without' && role.assignment_count) return false;
      if (!term) return true;
      return [
        role.name, role.code, role.description, typeLabel(role.object_type),
        role.priority != null ? String(role.priority) : '',
      ].filter(Boolean).join(' ').toLowerCase().includes(term);
    });
  }, [roles, search, filterType, filterStatus, filterScope, filterAssigned, typeLabel]);

  const clearFilters = () => {
    setFilterType(''); setFilterStatus(''); setFilterScope('');
    setFilterAssigned(''); setSearch('');
  };

  /* ── KPIs: chips que filtran al pulsarlos ───────────────────────────────── */
  const cuenta = useMemo(() => ({
    total:    roles.length,
    active:   roles.filter(r => r.is_active !== false).length,
    inactive: roles.filter(r => r.is_active === false).length,
    global:   roles.filter(r => !r.company_id).length,
    assigned: roles.filter(r => r.assignment_count).length,
  }), [roles]);

  const kpis = (
    <div className="list-kpis list-kpis--chips">
      <button type="button" className="list-kpi-chip" onClick={clearFilters}
        title={tr('roles.kpi_all', 'Ver todos los roles')}>
        <span className="list-kpi-value">{cuenta.total}</span>
        <span className="list-kpi-label">{tr('roles.plural', 'roles')}</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterStatus('active'); }}
        title={tr('roles.kpi_active', 'Ver los que están activos')}>
        <span className="list-kpi-value">{cuenta.active}</span>
        <span className="list-kpi-label">{tr('roles.active_plural', 'activos')}</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterStatus('inactive'); }}
        title={tr('roles.kpi_inactive', 'Ver los desactivados')}>
        <span className="list-kpi-value">{cuenta.inactive}</span>
        <span className="list-kpi-label">{tr('roles.inactive_plural', 'inactivos')}</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterScope('global'); }}
        title={tr('roles.kpi_global', 'Ver los roles globales (los comparten todas las empresas)')}>
        <span className="list-kpi-value">{cuenta.global}</span>
        <span className="list-kpi-label">{tr('roles.scope_global_plural', 'globales')}</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterAssigned('with'); }}
        title={tr('roles.kpi_assigned', 'Ver los que están puestos en algún equipo')}>
        <span className="list-kpi-value">{cuenta.assigned}</span>
        <span className="list-kpi-label">{tr('roles.assigned_plural', 'en uso')}</span>
      </button>
    </div>
  );

  /* ── columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: tr('roles.name', 'Nombre'),
        sortable: true,
        render: (r) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>{r.name}</span>
            {r.description && <span className="list-hint">{r.description}</span>}
          </span>
        ),
      },
      {
        key: 'code',
        label: tr('roles.code', 'Código'),
        sortable: true,
        render: (r) => <span className="list-code">{r.code}</span>,
      },
      {
        key: 'object_type',
        label: tr('roles.object_type', 'Tipo de objeto'),
        sortable: true,
        sortValue: (r) => typeLabel(r.object_type),
        render: (r) => <span className="list-badge">{typeLabel(r.object_type) || r.object_type}</span>,
      },
      {
        key: 'scope',
        label: tr('roles.scope', 'Ámbito'),
        sortable: true,
        sortValue: (r) => (r.company_id ? 'company' : 'global'),
        render: (r) => (r.company_id
          ? <span className="list-badge">{tr('roles.scope_company', 'Compañía')}</span>
          : (
            <span className="list-badge list-badge--warn" title={tr('roles.scope_global_hint', 'Lo comparten todas las empresas')}>
              {tr('roles.scope_global', 'Global')}
            </span>
          )),
      },
      {
        key: 'priority',
        label: tr('roles.priority', 'Prioridad'),
        sortable: true,
        sortValue: (r) => r.priority ?? 0,
        render: (r) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span className="list-code">{r.priority ?? '—'}</span>
            {r.priority != null && <span className="list-hint">{PRIORITY_LABEL(r.priority)}</span>}
          </span>
        ),
      },
      {
        key: 'assignments',
        label: tr('roles.assigned', 'Equipos'),
        sortable: true,
        sortValue: (r) => r.assignment_count ?? 0,
        render: (r) => {
          const asignable = ASSIGNABLE_TYPES.includes(r.object_type);
          if (!asignable) {
            return (
              <span className="list-hint" title={tr('roles.not_assignable', 'Este tipo de objeto todavía no se asigna a equipos')}>
                —
              </span>
            );
          }
          return (
            <button
              type="button"
              className="list-badge"
              style={{ cursor: 'pointer' }}
              onClick={(event) => { event.stopPropagation(); openTargets(r); }}
              title={tr('roles.view_targets', 'Ver los equipos con este rol')}
            >
              {r.assignment_count ?? 0}
            </button>
          );
        },
      },
      {
        key: 'is_active',
        label: tr('status.active', 'Activo'),
        sortable: true,
        sortValue: (r) => (r.is_active === false ? 0 : 1),
        render: (r) => (r.is_active === false
          ? <span className="list-badge list-badge--off">{tr('roles.inactive', 'Inactivo')}</span>
          : <span className="list-badge list-badge--on">{tr('roles.active', 'Activo')}</span>),
      },
      {
        key: 'description',
        label: tr('roles.description', 'Descripción'),
        render: (r) => r.description || dash,
      },
      {
        key: 'created_at',
        label: tr('roles.created', 'Creado'),
        sortable: true,
        render: (r) => (r.created_at ? r.created_at.slice(0, 10) : dash),
      },
      {
        key: 'updated_at',
        label: tr('roles.updated', 'Modificado'),
        sortable: true,
        render: (r) => (r.updated_at ? r.updated_at.slice(0, 10) : dash),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, typeLabel]);

  const defaultVisible = ['name', 'code', 'object_type', 'scope', 'priority', 'assignments', 'is_active'];

  const filters = [
    {
      key: 'type', label: tr('roles.object_type', 'Tipo de objeto'),
      allLabel: tr('roles.object_type_all', 'Todos los tipos'),
      value: filterType, onChange: setFilterType,
      options: OBJECT_TYPES.map(value => ({ value, label: typeLabel(value) })),
    },
    {
      key: 'status', label: tr('roles.status', 'Estado'),
      allLabel: tr('roles.status_all', 'Activos e inactivos'),
      value: filterStatus, onChange: setFilterStatus,
      options: [
        { value: 'active', label: tr('roles.only_active', 'Solo los activos') },
        { value: 'inactive', label: tr('roles.only_inactive', 'Solo los desactivados') },
      ],
    },
    {
      key: 'scope', label: tr('roles.scope', 'Ámbito'),
      allLabel: tr('roles.scope_all', 'Cualquier ámbito'),
      value: filterScope, onChange: setFilterScope,
      options: [
        { value: 'company', label: tr('roles.only_company', 'Solo de la empresa') },
        { value: 'global', label: tr('roles.only_global', 'Solo globales') },
      ],
    },
    {
      key: 'assigned', label: tr('roles.assigned', 'Equipos'),
      allLabel: tr('roles.assigned_all', 'Con o sin equipos'),
      value: filterAssigned, onChange: setFilterAssigned,
      options: [
        { value: 'with', label: tr('roles.assigned_with', 'Puestos en algún equipo') },
        { value: 'without', label: tr('roles.assigned_without', 'Sin asignar') },
      ],
    },
    {
      key: 'group', label: tr('roles.group', 'Agrupar por'),
      allLabel: tr('roles.group_none', 'Sin agrupar'),
      value: groupMode, onChange: setGroupMode,
      options: [
        { value: 'type', label: tr('roles.object_type', 'Tipo de objeto') },
        { value: 'status', label: tr('roles.status', 'Estado') },
        { value: 'scope', label: tr('roles.scope', 'Ámbito') },
      ],
    },
  ];

  const groupBy = useMemo(() => {
    if (groupMode === 'type') return (r) => typeLabel(r.object_type) || r.object_type;
    if (groupMode === 'status') {
      return (r) => (r.is_active === false ? tr('roles.inactive', 'Inactivo') : tr('roles.active', 'Activo'));
    }
    if (groupMode === 'scope') {
      return (r) => (r.company_id ? tr('roles.scope_company', 'Compañía') : tr('roles.scope_global', 'Global'));
    }
    return undefined;
  }, [groupMode, typeLabel, tr]);

  const subtitle = `${roles.length} ${tr('roles.plural', 'roles')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtrados')}`;

  const exportRows = useMemo(() => filtered.map(r => ({
    'Nombre': r.name || '',
    'Código': r.code || '',
    'Tipo de objeto': typeLabel(r.object_type),
    'Ámbito': r.company_id ? 'Compañía' : 'Global',
    'Prioridad': r.priority ?? '',
    'Equipos asignados': r.assignment_count ?? 0,
    'Estado': r.is_active === false ? 'Inactivo' : 'Activo',
    'Descripción': r.description || '',
    'Creado': r.created_at ? r.created_at.slice(0, 10) : '',
    'Modificado': r.updated_at ? r.updated_at.slice(0, 10) : '',
  })), [filtered, typeLabel]);

  /** Tarjetas: lo esencial del rol, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((r) => (
          <div
            key={r.id}
            className="list-card"
            role="button"
            tabIndex={0}
            onClick={() => openEdit(r)}
            onKeyDown={(event) => { if (event.key === 'Enter') openEdit(r); }}
            title={`${tr('action.edit', 'Editar')} ${r.name}`}
          >
            <div className="list-card-head">
              <div>
                <div className="list-card-title">{r.name}</div>
                <span className="list-hint list-code">{r.code}</span>
              </div>
              {r.is_active === false
                ? <span className="list-badge list-badge--off">{tr('roles.inactive', 'Inactivo')}</span>
                : <span className="list-badge list-badge--on">{tr('roles.active', 'Activo')}</span>}
            </div>

            <div className="list-card-meta">
              <span className="list-badge">{typeLabel(r.object_type) || r.object_type}</span>
              {!r.company_id && <span className="list-badge list-badge--warn">{tr('roles.scope_global', 'Global')}</span>}
              {r.priority != null && (
                <span className="list-hint">{tr('roles.priority', 'Prioridad')} {r.priority}</span>
              )}
              {ASSIGNABLE_TYPES.includes(r.object_type) && r.assignment_count > 0 && (
                <span className="list-hint">{r.assignment_count} {tr('roles.equipos_short', 'equipos')}</span>
              )}
            </div>

            {r.description && <p className="list-hint" style={{ margin: '0 0 6px' }}>{r.description}</p>}

            <div className="list-card-actions">{rowActions(r)}</div>
          </div>
        ))}
      </div>
    );
  }

  const ROLE_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada rol'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, tr]);

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">
          {tr('roles.select_company', 'Selecciona una empresa para ver sus roles.')}
        </div>
      </div>
    );
  }

  const codePreview = normalizeCode(form.code);
  const editingTargets = targetsOf;

  return (
    <>
      <ListPage
        icon={IdentificationIcon}
        title={tr('roles.title_long', 'Roles y permisos')}
        subtitle={subtitle}

        onCreate={openCreate}
        createLabel={tr('roles.new', 'Nuevo rol')}
        createTitle={tr('roles.new_hint', 'Un rol por tipo de objeto, para la nomenclatura y para etiquetar equipos')}

        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={tr('roles.search', 'Buscar por nombre, código o descripción…')}
        filters={filters}

        exportRows={exportRows}
        exportFileBase="roles"
        exportSheetName="Roles"

        columns={columns}
        columnStorageKey="cmdb_cols_roles"
        defaultVisible={defaultVisible}
        rows={filtered}
        rowKey={(r) => r.id}
        onRowClick={openEdit}
        rowTitle={(r) => `${tr('action.edit', 'Editar')} ${r.name}`}
        groupBy={groupBy}

        kpis={kpis}

        headerActions={notice && (
          <span className="list-badge list-badge--on" title={notice}>{notice}</span>
        )}

        views={ROLE_VIEWS}
        listKey="roles"
        defaultView="list"

        loading={loading}
        loadingText={tr('msg.loading', 'Cargando roles…')}
        error={error}

        empty={{
          icon: '🔑',
          title: roles.length === 0
            ? tr('roles.empty', 'Aún no hay roles. Crea el primero.')
            : tr('msg.no_results', 'Ningún registro coincide con los filtros'),
          subtitle: roles.length === 0
            ? tr('roles.empty_hint', 'Los roles de tipo servidor se asignan a los servidores y se usan en la nomenclatura.')
            : undefined,
          action: roles.length === 0 ? (
            <button type="button" className="list-btn-primary" onClick={openCreate}>
              ＋ {tr('roles.new', 'Nuevo rol')}
            </button>
          ) : undefined,
        }}

        actions={{
          extra: (r) => (
            <button
              type="button"
              className="list-action-btn"
              disabled={!ASSIGNABLE_TYPES.includes(r.object_type)}
              onClick={() => openTargets(r)}
              title={ASSIGNABLE_TYPES.includes(r.object_type)
                ? tr('roles.view_targets', 'Ver los equipos con este rol')
                : tr('roles.not_assignable', 'Este tipo de objeto todavía no se asigna a equipos')}
            >
              <ServerStackIcon />
            </button>
          ),
          onCopy: openCopy,
          onEdit: openEdit,
          onDelete: handleDelete,
          deleteTitle: tr('roles.delete_title', 'Desactivar rol'),
          deleteMessage: (r) => (
            <>
              {tr('roles.delete_confirm_short', '¿Seguro que quieres desactivar')} <strong>{r.name}</strong>?{' '}
              {tr('roles.delete_warning', 'El rol no se borra: deja de ofrecerse al dar de alta equipos, pero se conserva porque los que ya lo tienen puesto lo siguen apuntando.')}
              {!r.company_id && <>{' '}<strong>{tr('roles.delete_global', 'Es un rol GLOBAL: se desactiva para todas las empresas.')}</strong></>}
            </>
          ),
        }}
      />

      {/* ══ Diálogo: alta / edición ══ */}
      {formOpen && (
        <Dialog
          title={editingRole
            ? tr('roles.edit', 'Editar rol')
            : tr('roles.new', 'Nuevo rol')}
          icon={<IdentificationIcon style={{ width: 18, height: 18 }} />}
          maxWidth={560}
          busy={saving}
          onClose={closeForm}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={closeForm} disabled={saving}>
                {tr('action.cancel', 'Cancelar')}
              </button>
              <button type="button" className="list-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? tr('msg.saving', 'Guardando…') : tr('action.save', 'Guardar')}
              </button>
            </>
          )}
        >
          <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {formError && (
              <div className="alert alert-danger" style={{ margin: 0, padding: '6px 10px', fontSize: '0.82rem' }}>
                {formError}
              </div>
            )}
            {copiedFrom && (
              <p className="list-hint" style={{ margin: 0 }}>
                {tr('roles.copy_hint', 'Copia de')} <strong>{copiedFrom.name}</strong>{' '}
                {tr('roles.copy_hint_2', '· el código lleva el sufijo _COPIA porque es único por tipo de objeto.')}
              </p>
            )}

            <label className="list-hint">
              {tr('roles.name', 'Nombre')}
              <input
                className="list-input"
                style={{ width: '100%', marginTop: 4 }}
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder={tr('roles.name_ph', 'ej. Controlador de dominio')}
                autoFocus
              />
            </label>

            <div style={{ display: 'flex', gap: 10 }}>
              <label className="list-hint" style={{ flex: 1 }}>
                {tr('roles.code', 'Código')}
                <input
                  className="list-input"
                  style={{ width: '100%', marginTop: 4 }}
                  value={form.code}
                  onChange={(e) => setForm(f => ({ ...f, code: e.target.value }))}
                  placeholder="DC"
                />
                <span className="list-hint">
                  {tr('roles.code_hint', 'Se guarda como')} <span className="list-code">{codePreview || '—'}</span>
                  {' '}{tr('roles.code_hint_2', '· es único dentro de cada tipo de objeto.')}
                </span>
              </label>
              <label className="list-hint" style={{ width: 140 }}>
                {tr('roles.priority', 'Prioridad')}
                <input
                  type="number"
                  className="list-input"
                  style={{ width: '100%', marginTop: 4 }}
                  value={form.priority}
                  onChange={(e) => setForm(f => ({ ...f, priority: e.target.value }))}
                  placeholder="100"
                />
                <span className="list-hint">{tr('roles.priority_hint', 'A más número, antes se propone.')}</span>
              </label>
            </div>

            <label className="list-hint">
              {tr('roles.object_type', 'Tipo de objeto')}
              <select
                className="list-select"
                style={{ width: '100%', marginTop: 4 }}
                value={form.object_type}
                onChange={(e) => setForm(f => ({ ...f, object_type: e.target.value }))}
              >
                {OBJECT_TYPES.map(value => (
                  <option key={value} value={value}>
                    {typeLabel(value)}
                    {ASSIGNABLE_TYPES.includes(value)
                      ? ` · ${tr('roles.assignable', 'se puede asignar a equipos')}`
                      : ''}
                  </option>
                ))}
              </select>
              {!ASSIGNABLE_TYPES.includes(form.object_type) && (
                <span className="list-hint">
                  {tr('roles.object_type_hint', 'Solo los roles de servidor se pueden asignar hoy a un equipo; el resto quedan catalogados y disponibles para la nomenclatura.')}
                </span>
              )}
            </label>

            <label className="list-hint">
              {tr('roles.description', 'Descripción')}
              <textarea
                className="list-input"
                style={{ width: '100%', marginTop: 4, minHeight: 60, resize: 'vertical' }}
                value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder={tr('roles.description_ph', 'Para qué sirve este rol (opcional)')}
              />
            </label>

            <label className="list-hint" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={form.is_active !== false}
                onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))}
              />
              {tr('roles.active_hint', 'Activo (si lo desmarcas deja de ofrecerse, sin borrar nada)')}
            </label>
          </form>
        </Dialog>
      )}

      {/* ══ Diálogo: equipos con el rol ══ */}
      {editingTargets && (
        <Dialog
          title={`${tr('roles.targets_of', 'Equipos con el rol')} ${editingTargets.name || ''}`}
          icon={<ServerStackIcon style={{ width: 18, height: 18 }} />}
          maxWidth={620}
          onClose={() => setTargetsOf(null)}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={() => setTargetsOf(null)}>
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          {targetsLoading && <p style={{ margin: 0 }}>{tr('msg.loading', 'Cargando…')}</p>}
          {!targetsLoading && targets.length === 0 && (
            <p className="list-hint" style={{ margin: 0 }}>
              {tr('roles.no_targets', 'Ningún equipo tiene este rol puesto todavía. Se asigna desde la ficha del servidor.')}
            </p>
          )}
          {!targetsLoading && targets.length > 0 && targets.map(eq => (
            <div
              key={eq.object_id}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                padding: '6px 0', borderBottom: '1px solid var(--border)',
              }}
            >
              <span style={{ fontWeight: 600 }}>{eq.name}</span>
              {eq.serial_number && <span className="list-code">{eq.serial_number}</span>}
              {eq.location_name && <span className="list-hint">{eq.location_name}</span>}
              {eq.rack_name && <span className="list-badge">{eq.rack_name}</span>}
              <button
                type="button"
                className="list-action-btn"
                style={{ marginLeft: 'auto' }}
                onClick={() => navigate(`/infrastructure/servers?destacar=${eq.object_id}&abrir=1`)}
                title={tr('roles.view_server', 'Ver el servidor')}
              >
                <PencilSquareIcon />
              </button>
            </div>
          ))}
          {!targetsLoading && targets.length === 0 && (
            <p className="list-hint" style={{ marginTop: 10 }}>
              <PlusIcon style={{ width: 14, height: 14, verticalAlign: '-2px' }} />{' '}
              {tr('roles.targets_hint', 'Servidores → abre la ficha de un servidor → Roles.')}
            </p>
          )}
        </Dialog>
      )}
    </>
  );
}
